/**
 * Revisiones.
 *
 * Una revisión sin cerrar no tiene acta, y sin acta **no hay prueba de que
 * se hiciera**. Por eso el filtro por defecto son las abiertas: es trabajo
 * hecho que todavía no cuenta.
 */

import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';
import { day } from '../table/format.js';

interface Row {
  id: string;
  number: string;
  inspection_type: string;
  status: string;
  performed_on: string;
  customer_name: string;
  site_name: string;
  technician_name: string | null;
  planned_equipment: number;
  systems_covered: string[];
  anomaly_count: string;
}

const COLUMNS: Column<Row>[] = [
  { id: 'number', label: 'Nº', render: (r) => r.number, sortable: true, width: '11rem' },
  { id: 'customerName', label: 'Cliente', render: (r) => r.customer_name },
  { id: 'siteName', label: 'Centro', render: (r) => r.site_name },
  { id: 'technicianName', label: 'Técnico', render: (r) => r.technician_name ?? 'Sin asignar' },
  { id: 'inspectionType', label: 'Tipo', render: (r) => r.inspection_type, width: '10rem' },
  {
    id: 'status',
    label: 'Estado',
    width: '9rem',
    sortable: true,
    render: (r) => (
      <span className={`pill pill--${r.status === 'CERRADA' ? 'ok' : r.status === 'ANULADA' ? 'off' : 'warn'}`}>
        {r.status === 'CERRADA' ? '✓ Cerrada' : r.status === 'ANULADA' ? '✕ Anulada' : `◷ ${r.status}`}
      </span>
    ),
    exportValue: (r) => r.status,
  },
  { id: 'performedOn', label: 'Fecha', render: (r) => day(r.performed_on), sortable: true, width: '8rem' },
  {
    id: 'plannedEquipment',
    label: 'Equipos',
    numeric: true,
    width: '6rem',
    render: (r) => r.planned_equipment,
  },
  {
    id: 'anomalyCount',
    label: 'Anomalías',
    numeric: true,
    width: '7rem',
    render: (r) =>
      Number(r.anomaly_count) > 0 ? (
        <span className="due due--soon">⚠ {r.anomaly_count}</span>
      ) : (
        <span className="due--none">0</span>
      ),
    exportValue: (r) => r.anomaly_count,
  },
  {
    id: 'systemsCovered',
    label: 'Sistemas',
    render: (r) => (r.systems_covered ?? []).join(', ') || '—',
    exportValue: (r) => (r.systems_covered ?? []).join(' '),
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'open', label: 'Sin cerrar', param: { openOnly: 'true' } },
  { id: 'all', label: 'Todas', param: {} },
] as const;

export function InspectionsScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const listing = useListing<Row>({
    path: '/inspections',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Revisiones</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === 'open' ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {listing.total !== null && <span className="screen__count">{listing.total} revisiones</span>}
      </header>

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName="revisiones"
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        onOpenRow={(row) => void navigate(`/revisiones/${row.id}`)}
        emptyMessage="No hay revisiones que coincidan."
      />
    </section>
  );
}
